import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { handleCreateUser, handleGetUsers, handleUpdateUser, handleDeleteUsers } from './userManagementThunk';
import { IUser } from '~/apis/userManagement';
import { DEFAULT_PAGE, ROWS_PER_PAGE } from '~/utils/constants/common';

export interface UserManagementState {
  users: Array<IUser>;
  currentUser: IUser | null;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  loading: boolean;
  error: string | null;
  filter: IUserFilter;
  selectedIds: Array<string>;
  openAlertDelete: boolean;
  needRefreshData: boolean;
}

interface IUserFilter {
  page?: number;
  limit?: number;
  search?: string;
  searchFields?: Array<string>;
  sort?: Array<{ field: string; direction: 'asc' | 'desc' }>;
}

const initialState: UserManagementState = {
  users: [],
  currentUser: null,
  currentPage: DEFAULT_PAGE,
  totalPages: 0,
  totalItems: 0,
  loading: false,
  error: null,
  filter: {
    page: 1,
    limit: ROWS_PER_PAGE,
    search: '',
    searchFields: [],
    sort: [],
  },
  selectedIds: [],
  openAlertDelete: false,
  needRefreshData: false,
};

const userManagementSlice = createSlice({
  name: 'userManagement',
  initialState,
  reducers: {
    setFilter(state, action: PayloadAction<IUserFilter>) {
      state.filter = { ...state.filter, ...action.payload };
    },
    changeSelectedIds(state, action: PayloadAction<Array<string>>) {
      state.selectedIds = [...action.payload];
    },
    changeOpenAlertDelete(state, action: PayloadAction<boolean>) {
      state.openAlertDelete = action.payload;
    },
    changeNeedRefreshData(state, action: PayloadAction<boolean>) {
      state.needRefreshData = action.payload;
    },
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers(builder) {
    builder
      // Get Users
      .addCase(handleGetUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(handleGetUsers.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.responses && Array.isArray(action.payload.responses)) {
          state.users = [...action.payload.responses];
        } else if (Array.isArray(action.payload)) {
          state.users = [...action.payload];
        } else {
          state.users = [];
        }
        
        if (action.payload.pagination) {
          state.currentPage = action.payload.pagination.page;
          state.totalPages = action.payload.pagination.totalPages;
          state.totalItems = action.payload.pagination.totalItems;
        } else {
          state.currentPage = 1;
          state.totalPages = 1;
          state.totalItems = state.users.length;
        }
        
        state.needRefreshData = false;
      })
      .addCase(handleGetUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as any)?.message || 'An error occurred while fetching users';
      })
      // Create User
      .addCase(handleCreateUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(handleCreateUser.fulfilled, (state) => {
        state.loading = false;
        state.needRefreshData = true;
      })
      .addCase(handleCreateUser.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as any)?.message || 'An error occurred while creating user';
      })
      // Update User
      .addCase(handleUpdateUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(handleUpdateUser.fulfilled, (state) => {
        state.loading = false;
        state.needRefreshData = true;
      })
      .addCase(handleUpdateUser.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as any)?.message || 'An error occurred while updating user';
      })
      // Delete Users
      .addCase(handleDeleteUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(handleDeleteUsers.fulfilled, (state) => {
        state.loading = false;
        state.needRefreshData = true;
        state.openAlertDelete = false;
      })
      .addCase(handleDeleteUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as any)?.message || 'An error occurred while deleting user';
      });
  },
});

export const userManagementActions = userManagementSlice.actions;
const userManagementReducer = userManagementSlice.reducer;
export default userManagementReducer;
